import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import {
  ChevronLeft,
  Bot,
  Check,
  Sparkles,
  Loader,
  Users,
  AlertTriangle,
  Shield
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getApiErrorMessage, resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import { uploadFilesAndComplete } from '../utils/fileUpload';
import { MODULE_UPLOAD } from '../utils/moduleUpload.ts';
import { useExtractProcessesMutation } from '../queries/processes/aiExtraction';
import ProcessFlowchartVisualization from './ProcessFlowchartVisualization';
import FlowchartViewer from './FlowchartViewer';
import { AiDocumentLibrary } from './processes/AiDocumentLibrary';
import { ProcessDocumentUploadZone } from './processes/ProcessDocumentUploadZone';
import {
  aiProcessDocumentFileSchema
  // aiProcessDocumentTagsSchema
} from './processes/schema';
import { AI_LIBRARY_FILE_CAP, AI_LOADING_MESSAGES } from './processes/constants';
import { documentRowKey } from '../utils/documentUtils';
import { normalizeAiExtractedProcessRows } from './processes/functional';

function AISOXProcessDesigner({
  cycleId,
  cycleName,
  onBack,
  onComplete,
  onExtractedProcesses,
  embedded = false,
  documents: controlledDocuments,
  onDocumentsChange,
  /** When false, rows are selectable before upload (create process AI library). Default true. */
  selectionRequiresLinkedFile = true,
  /** Initial checkbox state for newly added library files. */
  defaultDocumentSelected = true
  // activeTagDocumentId: controlledActiveTagDocumentId,
  // onActiveTagDocumentIdChange
}) {
  const navigate = useNavigate();
  const extractProcessesMutation = useExtractProcessesMutation();
  const [currentStep, setCurrentStep] = useState('choose-method'); // choose-method, process-selection, visualization, flowchart
  const [soxProcess] = useState(cycleName || 'Order to Cash'); // Initialize with cycleName if provided
  const [isGeneratingFlowchart, setIsGeneratingFlowchart] = useState(false);
  const [generationError, setGenerationError] = useState(null);
  const [identifiedProcesses, setIdentifiedProcesses] = useState([]);
  const [selectedProcesses, setSelectedProcesses] = useState([]);
  const [generatedFlowcharts, setGeneratedFlowcharts] = useState([]);
  const [processDetails] = useState({});
  const [flowchartData] = useState(null);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [internalDocuments, setInternalDocuments] = useState([]);
  // const [internalActiveTagDocumentId, setInternalActiveTagDocumentId] = useState(null);
  const aiDocuments = controlledDocuments ?? internalDocuments;
  const setAiDocuments = onDocumentsChange ?? setInternalDocuments;
  // const activeTagDocumentId = controlledActiveTagDocumentId ?? internalActiveTagDocumentId;
  // const setActiveTagDocumentId = onActiveTagDocumentIdChange ?? setInternalActiveTagDocumentId;

  const selectedAiDocuments = useMemo(
    () => aiDocuments.filter((doc) => doc.selected),
    [aiDocuments]
  );
  const selectedAiDocumentCount = selectedAiDocuments.length;
  const libraryAtCapacity = aiDocuments.length >= AI_LIBRARY_FILE_CAP;
  const canAddMoreFiles = !libraryAtCapacity && !isGeneratingFlowchart;
  const selectedDocsReadyForExtract = selectedAiDocuments.length > 0;

  // Rotate loading messages every 5 seconds
  useEffect(() => {
    if (!isGeneratingFlowchart) {
      setCurrentMessageIndex(0); // Reset to first message when not loading
      return;
    }

    const interval = setInterval(() => {
      setCurrentMessageIndex((prevIndex) => (prevIndex + 1) % AI_LOADING_MESSAGES.length);
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, [isGeneratingFlowchart]);

  const addFilesToLibrary = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    const remainingSlots = AI_LIBRARY_FILE_CAP - aiDocuments.length;
    if (remainingSlots <= 0) {
      toast.error(`You can add up to ${AI_LIBRARY_FILE_CAP} files per cycle.`);
      return;
    }

    const rejectedMessages = new Map();
    const valid = files.filter((file) => {
      try {
        aiProcessDocumentFileSchema.validateSync(file);
        return true;
      } catch (error) {
        const message = error?.message || 'File failed validation.';
        rejectedMessages.set(message, (rejectedMessages.get(message) || 0) + 1);
        return false;
      }
    });
    const accepted = valid.slice(0, remainingSlots);

    rejectedMessages.forEach((count, message) => {
      toast.error(count > 1 ? `${message} (${count} files)` : message);
    });
    if (files.length - accepted.length > Array.from(rejectedMessages.values()).reduce((sum, count) => sum + count, 0)) {
      toast.error(`Only ${remainingSlots} more file(s) can be added.`);
    }
    if (accepted.length === 0) return;

    const newDocs = accepted.map((file) => ({
      id: `doc-${uuidv4()}`,
      file,
      file_id: null,
      name: file.name,
      size: file.size,
      tags: [],
      selected: defaultDocumentSelected
    }));

    setAiDocuments((prev) => [...prev, ...newDocs]);
    toast.success(`${newDocs.length} ${newDocs.length > 1 ? 'files' : 'file'} added to cycle library.`);
  };

  const toggleDocumentSelection = useCallback((rowKey, checked) => {
    setAiDocuments((prev) =>
      prev.map((doc) => (documentRowKey(doc) === rowKey ? { ...doc, selected: checked } : doc))
    );
  }, [setAiDocuments]);

  const removeDocument = useCallback((rowKey) => {
    setAiDocuments((prev) => prev.filter((doc) => documentRowKey(doc) !== rowKey));
  }, [setAiDocuments]);

  // const removeDocumentTag = (rowKey, tagToRemove) => {
  //   setAiDocuments((prev) =>
  //     prev.map((doc) =>
  //       matchDocumentByRowKey(doc, rowKey)
  //         ? { ...doc, tags: (doc.tags || []).filter((tag) => tag !== tagToRemove) }
  //         : doc
  //     )
  //   );
  // };

  // const closeTagCreator = () => {
  //   setActiveTagDocumentId(null);
  // };

  // const addDocumentTag = (rowKey, inputValue) => {
  //   const tag = String(inputValue || '').trim();
  //   if (!tag) return false;
  //   const currentDoc = aiDocuments.find((doc) => matchDocumentByRowKey(doc, rowKey));
  //   if (!currentDoc) return false;

  //   try {
  //     aiProcessDocumentTagsSchema.validateSync([...(currentDoc.tags || []), tag]);
  //   } catch (error) {
  //     const message = error?.message || 'Invalid tag';
  //     toast.error(message);
  //     return false;
  //   }

  //   setAiDocuments((prev) =>
  //     prev.map((doc) =>
  //       matchDocumentByRowKey(doc, rowKey) ? { ...doc, tags: [...(doc.tags || []), tag] } : doc
  //     )
  //   );
  //   return true;
  // };

  const toggleProcessSelection = (processId) => {
    setSelectedProcesses(prev => {
      if (prev.includes(processId)) {
        return prev.filter(id => id !== processId);
      } else {
        return [...prev, processId];
      }
    });
    
    setIdentifiedProcesses(prev => 
      prev.map(p => 
        p.id === processId ? { ...p, isSelected: !p.isSelected } : p
      )
    );
  };

  const handleGenerateFlowcharts = () => {
    const selected = identifiedProcesses.filter(p => selectedProcesses.includes(p.id));
    setGeneratedFlowcharts(selected);
    setCurrentStep('visualization');
  };

  const handleSelectAll = () => {
    setSelectedProcesses(identifiedProcesses.map(p => p.id));
    setIdentifiedProcesses(prev => prev.map(p => ({ ...p, isSelected: true })));
  };

  const handleDeselectAll = () => {
    setSelectedProcesses([]);
    setIdentifiedProcesses(prev => prev.map(p => ({ ...p, isSelected: false })));
  };

  const uploadDocumentForExtraction = async (doc, userHash) => {
    const [uploaded] = await uploadFilesAndComplete([doc.file], userHash, MODULE_UPLOAD.SOX_PROCESS);
    if (!uploaded?.file_id) {
      throw new Error(`Upload did not return a file id for ${doc.name}`);
    }
    return { document_id: doc.id, file_id: uploaded.file_id };
  };

  const handleGenerateProcessFlow = async () => {
    const selectedDocs = aiDocuments.filter((doc) => doc.selected);
    if (selectedDocs.length === 0) {
      toast.error('Select at least one document to extract processes.');
      return;
    }

    setIsGeneratingFlowchart(true);
    setGenerationError(null);

    try {
      const userHash = localStorage.getItem('userHash') || '';
      const docsMissingFileId = selectedDocs.filter((doc) => !doc.file_id);
      const uploadedFileIdsByDocumentId = {};

      if (docsMissingFileId.length > 0) {
        const uploadResults = await Promise.allSettled(
          docsMissingFileId.map((doc) => uploadDocumentForExtraction(doc, userHash))
        );

        uploadResults.forEach((result, index) => {
          const doc = docsMissingFileId[index];
          if (result.status === 'fulfilled') {
            uploadedFileIdsByDocumentId[result.value.document_id] = result.value.file_id;
            return;
          }
          toast.error(`Failed to upload ${doc.name}`);
        });

        setAiDocuments((prev) =>
          prev.map((doc) =>
            docsMissingFileId.some((pendingDoc) => pendingDoc.id === doc.id)
              ? {
                  ...doc,
                  file_id: uploadedFileIdsByDocumentId[doc.id] || doc.file_id,
                  uploadError: uploadedFileIdsByDocumentId[doc.id] ? null : 'Upload failed'
                }
              : doc
          )
        );
      }

      const documentsPayload = selectedDocs
        .map((doc) => ({
          file_id: doc.file_id || uploadedFileIdsByDocumentId[doc.id],
          file_name: doc.name,
          tags: doc.tags || []
        }))
        .filter((doc) => Boolean(doc.file_id));
      const extractionDocumentsPayload = documentsPayload.map((doc) => ({
        file_id: doc.file_id,
        tags: doc.tags
      }));
      const selectedFileIds = documentsPayload.map((doc) => doc.file_id).filter(Boolean);
      if (selectedFileIds.length === 0) {
        throw new Error('No selected files uploaded successfully.');
      }
      if (selectedFileIds.length !== selectedDocs.length) {
        toast.error('Some selected files failed upload and were skipped.');
      }

      const data = await extractProcessesMutation.mutateAsync({
        cycle_id: cycleId,
        cycle_name: cycleName || soxProcess,
        documents: extractionDocumentsPayload
      });
      const response = { data };

      const extractedProcesses = Array.isArray(response.data?.processes) ? response.data.processes : [];
      if (response.data?.success !== false) {
        const processesWithSourceDocuments = normalizeAiExtractedProcessRows(extractedProcesses, {
          file_ids: selectedFileIds,
          documents: documentsPayload
        });

        if (typeof onExtractedProcesses === 'function') {
          onExtractedProcesses(processesWithSourceDocuments, {
            file_ids: selectedFileIds,
            documents: documentsPayload
          });
          if (processesWithSourceDocuments.length === 0) {
            toast.error('No processes were found in the selected files.');
          } else {
            toast.success(`Extracted ${processesWithSourceDocuments.length} processes`);
          }
          return;
        }

        if (processesWithSourceDocuments.length === 0) {
          toast.error('No processes were found in the selected files.');
        } else {
          toast.success(`Extracted ${processesWithSourceDocuments.length} processes`);
        }
        navigate(`/sox-cycles/${cycleId}/processes`);
      } else {
        throw new Error(response.data.message || 'Failed to extract processes');
      }
    } catch (error) {
      console.error('Error extracting processes:', error);
      setGenerationError(resolve403ErrorMessage(error, getApiErrorMessage(error, 'An unexpected error occurred. Please try again.')));
      // Keep isGeneratingFlowchart true to show error overlay
    } finally {
      setIsGeneratingFlowchart(false);
    }
  };

  const handleRetryGeneration = () => {
    handleGenerateProcessFlow();
  };

  const handleCancelGeneration = () => {
    setGenerationError(null);
    setIsGeneratingFlowchart(false);
  };

  // Loading/Error Overlay Component
  const renderGenerationOverlay = () => {
    if (!isGeneratingFlowchart) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full mx-4">
          {generationError ? (
            // Error State
            <>
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertTriangle className="h-8 w-8 text-red-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center text-gray-900 mb-2">
                Failed to Generate Flowchart
              </h3>
              <p className="text-sm text-gray-600 text-center mb-4">
                The AI service encountered an error. Please try again.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
                <p className="text-sm text-red-800 font-mono">
                  {generationError}
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleCancelGeneration}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRetryGeneration}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Try Again
                </button>
              </div>
            </>
          ) : (
            // Loading State
            <>
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className="p-4 bg-purple-100 rounded-full">
                    <Bot className="h-12 w-12 text-purple-600 animate-pulse" />
                  </div>
                  <div className="absolute -bottom-1 -right-1 p-1 bg-white rounded-full shadow-lg">
                    <Loader className="h-5 w-5 text-purple-600 animate-spin" />
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center text-gray-900 mb-2">
                Skeptics AI is analyzing your process...
              </h3>
              <div className="min-h-[60px] flex items-center justify-center mb-6">
                <p
                  key={currentMessageIndex}
                  className="text-sm text-gray-600 text-center px-4 animate-fadeIn"
                >
                  {AI_LOADING_MESSAGES[currentMessageIndex]}
                </p>
              </div>
              {/* Indeterminate Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full animate-progress"></div>
              </div>

              <style>{`
                @keyframes fadeIn {
                  from {
                    opacity: 0;
                    transform: translateY(-10px);
                  }
                  to {
                    opacity: 1;
                    transform: translateY(0);
                  }
                }

                .animate-fadeIn {
                  animation: fadeIn 0.5s ease-out;
                }
              `}</style>
            </>
          )}
        </div>
      </div>
    );
  };

  // Choose Method Step
  if (currentStep === 'choose-method') {
    return (
      <div className={embedded ? '' : 'min-h-screen bg-gray-50'}>
        <div className={embedded ? '' : 'px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto'}>
          {/* Header */}
          {!embedded && (
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center space-x-4">
                <button onClick={onBack} className="text-gray-600 hover:text-gray-900">
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Bot className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">AI Process Designer</h2>
                    <p className="text-sm text-gray-500">Interactive process creation with real-time visual feedback</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className={embedded ? '' : 'max-w-4xl mx-auto'}>
            <div className="mb-4">
              <h3 className="text-base font-semibold text-gray-900">AI Assisted Process Extraction</h3>
              <p className="mt-1 text-sm text-gray-500">
                Add files to this cycle's library, select which to use, then extract processes for review.
              </p>
            </div>

            <div className="mb-4 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-700">
              SOX cycle: <span className="font-semibold text-gray-900">{soxProcess}</span>
            </div>

            <AiDocumentLibrary
              documents={aiDocuments}
              onToggleDocumentSelection={toggleDocumentSelection}
              onRemoveDocument={removeDocument}
              selectionRequiresLinkedFile={selectionRequiresLinkedFile}
            />

            <ProcessDocumentUploadZone
              variant="expanded"
              disabled={!canAddMoreFiles}
              hiddenWhenDisabled
              onFilesSelected={addFilesToLibrary}
            />

            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleGenerateProcessFlow}
                disabled={!selectedDocsReadyForExtract || isGeneratingFlowchart}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isGeneratingFlowchart ? <Loader className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Extract processes from selected ({selectedAiDocumentCount})
              </button>
            </div>
          </div>
        </div>
        {renderGenerationOverlay()}
      </div>
    );
  }

  // Process Selection Step
  if (currentStep === 'process-selection') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => setCurrentStep('choose-method')} 
                className="text-gray-600 hover:text-gray-900"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <div>
                <h2 className="text-xl font-semibold">Identified Business Processes</h2>
                <p className="text-sm text-gray-500">Select the processes you'd like to generate flowcharts for</p>
              </div>
            </div>
          </div>

          {/* Selection Summary */}
          <div className="bg-white rounded-lg p-4 mb-6 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {selectedProcesses.length} of {identifiedProcesses.length} processes selected
            </span>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleSelectAll}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Select All
              </button>
              <button
                onClick={handleDeselectAll}
                className="text-sm text-gray-600 hover:text-gray-700 font-medium"
              >
                Deselect All
              </button>
            </div>
          </div>

          {/* Processes List */}
          <div className="space-y-4 mb-8">
            {identifiedProcesses.map((process) => (
              <div
                key={process.id}
                onClick={() => toggleProcessSelection(process.id)}
                className={`bg-white rounded-lg p-6 cursor-pointer transition-all border-2 ${
                  process.isSelected 
                    ? 'border-blue-500 shadow-lg' 
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        process.isSelected 
                          ? 'bg-blue-500 border-blue-500' 
                          : 'border-gray-300'
                      }`}>
                        {process.isSelected && (
                          <Check className="h-3 w-3 text-white" />
                        )}
                      </div>
                      <h3 className="font-semibold text-lg">{process.name}</h3>
                    </div>
                    <p className="text-gray-600 mb-4 ml-8">{process.description}</p>
                    
                    {/* Process Details Grid */}
                    <div className="ml-8 space-y-4">
                      {process.steps && process.steps.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Extracted Process Steps:</p>
                          <div className="flex flex-wrap gap-2">
                            {process.steps.slice(0, 4).map((step, index) => {
                              // Extract the step content, removing any leading numbers
                              let stepContent = typeof step === 'string' ? step : `Step ${index + 1}`;
                              // Remove leading numbers and periods if they exist
                              stepContent = stepContent.replace(/^\d+\.\s*/, '');
                              
                              return (
                                <span
                                  key={index}
                                  className="px-3 py-1 bg-gray-100 text-sm text-gray-700 rounded-full"
                                >
                                  {`${index + 1}. ${stepContent}`}
                                </span>
                              );
                            })}
                            {process.steps.length > 4 && (
                              <span className="px-3 py-1 bg-gray-100 text-sm text-gray-700 rounded-full">
                                +{process.steps.length - 4} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Responsible Parties */}
                      {process.responsible_parties && process.responsible_parties.length > 0 && (
                        <div className="flex items-start space-x-2">
                          <Users className="h-4 w-4 text-gray-400 mt-0.5" />
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Responsible Parties:</p>
                            <div className="flex flex-wrap gap-2">
                              {process.responsible_parties.map((party, index) => (
                                <span key={index} className="text-sm text-gray-600">
                                  {party}{index < process.responsible_parties.length - 1 ? ',' : ''}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Risks */}
                      {process.risks && process.risks.length > 0 && (
                        <div className="flex items-start space-x-2">
                          <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5" />
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Identified Risks:</p>
                            <div className="flex flex-wrap gap-2">
                              {process.risks.slice(0, 3).map((risk, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-yellow-50 text-xs text-yellow-800 rounded"
                                >
                                  {risk}
                                </span>
                              ))}
                              {process.risks.length > 3 && (
                                <span className="text-xs text-gray-500">+{process.risks.length - 3} more</span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Controls */}
                      {process.controls && process.controls.length > 0 && (
                        <div className="flex items-start space-x-2">
                          <Shield className="h-4 w-4 text-green-500 mt-0.5" />
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Controls:</p>
                            <div className="flex flex-wrap gap-2">
                              {process.controls.slice(0, 3).map((control, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-green-50 text-xs text-green-800 rounded"
                                >
                                  {control}
                                </span>
                              ))}
                              {process.controls.length > 3 && (
                                <span className="text-xs text-gray-500">+{process.controls.length - 3} more</span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Generate Button */}
          <div className="flex justify-center">
            <button
              onClick={handleGenerateFlowcharts}
              disabled={selectedProcesses.length === 0}
              className={`px-8 py-3 rounded-lg font-medium flex items-center space-x-2 ${
                selectedProcesses.length > 0
                  ? 'bg-gray-900 text-white hover:bg-gray-800'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Sparkles className="h-5 w-5" />
              <span>Generate Flowcharts ({selectedProcesses.length})</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Visualization Step
  if (currentStep === 'visualization') {
    return (
      <ProcessFlowchartVisualization
        selectedProcesses={generatedFlowcharts.map(p => p.id)}
        processDetails={processDetails}
        onBack={() => setCurrentStep('process-selection')}
        showChat={true}
      />
    );
  }

  // Flowchart Step
  if (currentStep === 'flowchart') {
    return (
      <FlowchartViewer
        flowchartData={flowchartData}
        onBack={() => setCurrentStep('choose-method')}
        onComplete={onComplete}
        cycleId={cycleId}
        processId={null} // Will be generated on first save
      />
    );
  }

  return null;
}

export default AISOXProcessDesigner;